import { useCallback, useEffect, useMemo, useState } from 'react';
import { TimeSlotEntry } from '../types/booking';
import { ApiData } from '../types/rest';
import { extractDateFromISOString, getLocalHour, timeSlotSpansOneDay } from '../utils/dates';

interface UseDateParams {
	availabilities: TimeSlotEntry[];
	setTimeSlot: (timeSlot: TimeSlotEntry | null) => void;
	settings: ApiData['settings'];
	timeSlot: TimeSlotEntry | null;
	timezone: string; // IANA timezone like "Europe/Berlin"
}

const getInitialDate = (availabilities: TimeSlotEntry[], timeSlot: TimeSlotEntry | null): string => {
	// Guard against empty availabilities
	if (!availabilities?.length) return '';

	if (timeSlot) {
		const currentTimeSlotDate = extractDateFromISOString(timeSlot.start_datetime);
		const isDateAvailable = availabilities.some((avail) => extractDateFromISOString(avail.start_datetime) === currentTimeSlotDate);

		if (isDateAvailable) {
			return currentTimeSlotDate;
		}
	}

	return extractDateFromISOString(availabilities[0].start_datetime);
};

export const useDate = ({ availabilities, setTimeSlot, settings, timeSlot, timezone }: UseDateParams) => {
	const [date, setDate] = useState<string>(() => getInitialDate(availabilities, timeSlot));

	// Memoize filtered time slots - single source of truth
	const filteredTimeSlots = useMemo(() => availabilities.filter((slot) => extractDateFromISOString(slot.start_datetime) === date), [availabilities, date]);

	const hasOnlyFullDayTimeSlots = useMemo(
		() =>
			filteredTimeSlots.every((slot) => {
				return timeSlotSpansOneDay(slot.start_datetime, slot.end_datetime, timezone);
			}),
		[filteredTimeSlots],
	);

	const findPreferredTimeSlot = useCallback(
		(timeSlots: TimeSlotEntry[]): TimeSlotEntry | null => {
			if (timeSlots.length === 0) {
				return null;
			}

			// Find the first time slot at or after the configured minimum hour in the slot's timezone
			const timeSlotAfterMinHour = timeSlots.find((slot) => {
				const hour = getLocalHour(slot.start_datetime, timezone);
				return hour >= settings.min_preferred_hour;
			});

			// If found, return it; otherwise return the last time slot
			return timeSlotAfterMinHour ?? timeSlots[timeSlots.length - 1];
		},
		[timezone, settings.min_preferred_hour],
	);

	// Update the selected time slot when the timeSlots or selection logic changes
	useEffect(() => {
		const selectedSlot = findPreferredTimeSlot(filteredTimeSlots);
		setTimeSlot(selectedSlot);
	}, [filteredTimeSlots, findPreferredTimeSlot, setTimeSlot]);

	return { date, hasOnlyFullDayTimeSlots, setDate, timeSlots: filteredTimeSlots };
};
